import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import {
  LogOut, ArrowLeft, Sparkles, Gauge, CheckCircle, Coins,
  ShieldCheck, Eye, Lock, Cpu, RefreshCw, FileText
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from 'recharts';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_DARK = "/benchmark-ph-dark.png";

function HeroStat({ label, value, testId }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10" data-testid={testId}>
      <div className="text-3xl font-bold text-accent">{value}</div>
      <div className="text-slate-400 text-xs mt-1">{label}</div>
    </div>
  );
}

function IntegrityCard({ icon: Icon, title, text, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 bg-accent/10 rounded-xl flex items-center justify-center">
          <Icon className="w-4.5 h-4.5 text-accent-dark w-5 h-5" />
        </div>
        <h3 className="font-semibold text-primary">{title}</h3>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}

export default function AITransparencyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { logout } = useAuth();

  const load = useCallback(async (s = '', e = '') => {
    setLoading(true);
    try {
      const params = {};
      if (s) params.start = s;
      if (e) params.end = e;
      const res = await api.get('/api/admin/telemetry/ai', { params });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load AI telemetry');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  const chartData = (data?.calls_daily || []).map((c, i) => ({
    label: c.label,
    calls: c.value,
    tokens: data?.tokens_daily?.[i]?.value ?? 0
  }));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Branded hero */}
      <section className="hero-gradient px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-10">
          <img src={BRAND_LOGO_DARK} alt="Benchmark PH" className="h-10 object-contain" />
          <div className="flex items-center gap-4">
            <Link to="/admin/telemetry" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm transition-colors" data-testid="back-to-telemetry">
              <ArrowLeft className="w-4 h-4" /> Telemetry
            </Link>
            <button onClick={logout} className="text-slate-400 hover:text-white p-2" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="inline-flex items-center gap-2 bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Sparkles className="w-4 h-4" /> Responsible AI Reporting
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
          AI Usage & Consumption <span className="text-accent">Transparency</span>
        </h1>
        <p className="text-slate-300 max-w-2xl mb-8">
          Full visibility into how AI is used across the Benchmark PH platform — consumption,
          performance, estimated cost, and the integrity guardrails governing every AI interaction.
        </p>

        {/* Date filter */}
        <div className="flex flex-wrap items-end gap-3 mb-8" data-testid="ai-date-filters">
          <div>
            <label className="block text-xs text-slate-400 mb-1">From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="h-10 px-3 rounded-xl border border-white/20 bg-white/10 text-white text-sm" data-testid="ai-start-date" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="h-10 px-3 rounded-xl border border-white/20 bg-white/10 text-white text-sm" data-testid="ai-end-date" />
          </div>
          <button onClick={() => load(startDate, endDate)} className="btn-accent h-10 px-5 flex items-center gap-2 text-sm" data-testid="ai-apply-btn">
            <RefreshCw className="w-4 h-4" /> Apply
          </button>
          <span className="text-xs text-slate-400 ml-2">Period: {data?.period?.start} → {data?.period?.end}</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="ai-hero-stats">
          <HeroStat label="Total AI Calls" value={(data?.total_calls ?? 0).toLocaleString()} testId="stat-ai-calls" />
          <HeroStat label="Success Rate" value={`${data?.success_rate_pct ?? 100}%`} testId="stat-success-rate" />
          <HeroStat label="Avg Latency" value={`${data?.avg_latency_ms ?? 0} ms`} testId="stat-avg-latency" />
          <HeroStat label="Input Tokens (est.)" value={(data?.input_tokens ?? 0).toLocaleString()} testId="stat-in-tokens" />
          <HeroStat label="Output Tokens (est.)" value={(data?.output_tokens ?? 0).toLocaleString()} testId="stat-out-tokens" />
          <HeroStat label="Est. Cost to Date" value={`$${(data?.estimated_cost_usd ?? 0).toFixed(4)}`} testId="stat-ai-cost" />
        </div>
      </section>

      <main className="px-6 md:px-12 py-10 max-w-7xl mx-auto space-y-8">
        {/* Consumption chart */}
        <div className="dashboard-card" data-testid="ai-consumption-chart">
          <h2 className="text-lg font-semibold text-primary mb-1">Daily AI Consumption</h2>
          <p className="text-sm text-slate-500 mb-4">AI calls (bars) and estimated tokens consumed (line)</p>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <RechartsTooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="calls" name="AI Calls" fill="#40D39B" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="tokens" name="Tokens (est.)" stroke="#7C3AED" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Usage by feature */}
        <div className="dashboard-card" data-testid="ai-feature-table">
          <h2 className="text-lg font-semibold text-primary mb-4">Consumption by AI Feature</h2>
          {(data?.by_feature || []).length === 0 ? (
            <p className="text-slate-400 text-sm py-6 text-center">No AI usage recorded in this period</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr><th>Feature</th><th className="text-right">Calls</th><th className="text-right">Input Tokens</th><th className="text-right">Output Tokens</th></tr>
              </thead>
              <tbody>
                {data.by_feature.map((f) => (
                  <tr key={f.feature}>
                    <td className="font-medium">{f.feature}</td>
                    <td className="text-right">{f.calls.toLocaleString()}</td>
                    <td className="text-right font-mono">{f.in_tokens.toLocaleString()}</td>
                    <td className="text-right font-mono">{f.out_tokens.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Integrity metrics */}
        <div>
          <h2 className="text-xl font-bold text-primary mb-1">AI Integrity & Governance</h2>
          <p className="text-sm text-slate-500 mb-5">The guardrails that keep AI use on this platform safe, scoped and accountable</p>
          <div className="grid md:grid-cols-2 gap-5" data-testid="ai-integrity-section">
            <IntegrityCard icon={Cpu} title="Model & Provider" testId="integrity-model"
              text={`${data?.integrity?.model}. ${data?.integrity?.provider}.`} />
            <IntegrityCard icon={ShieldCheck} title="Scope of Use" testId="integrity-scope"
              text={data?.integrity?.scope} />
            <IntegrityCard icon={Eye} title="Human Oversight" testId="integrity-oversight"
              text={data?.integrity?.human_oversight} />
            <IntegrityCard icon={Lock} title="Data Handling & Privacy" testId="integrity-privacy"
              text={data?.integrity?.data_handling} />
          </div>
          <div className="mt-5 flex items-start gap-3 p-4 bg-slate-100 rounded-xl text-sm text-slate-500" data-testid="token-note">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {data?.integrity?.token_note}
          </div>
        </div>

        {/* Performance strip */}
        <div className="dashboard-card !p-4 flex flex-wrap gap-6 items-center text-sm" data-testid="ai-performance-strip">
          <div className="flex items-center gap-2"><Gauge className="w-4 h-4 text-amber-500" /> P95 Latency: <span className="font-semibold">{data?.p95_latency_ms ?? 0} ms</span></div>
          <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Success Rate: <span className="font-semibold">{data?.success_rate_pct ?? 100}%</span></div>
          <div className="flex items-center gap-2"><Coins className="w-4 h-4 text-purple-500" /> Blended est. cost per call: <span className="font-semibold">${data?.total_calls ? (data.estimated_cost_usd / data.total_calls).toFixed(5) : '0.00000'}</span></div>
        </div>
      </main>

      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 hover:underline">
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
